import { CloseOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Empty, Input, message, Modal } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import LocalImage from '@/components/Common/LocalImage';
import {
  fetchInsCommentExport,
  fetchInsFamousPostCommentPostsData,
  fetchInsFamousPostSubCommentPostsData,
  sendInsFamousRequireAddRequest,
} from '@/lib/service/anonymously';
import { cn } from '@/lib/utils';
import {
  InstagramPostsCommentsItemModel,
  InstagramQueryPostItemModel,
  InstagramUserSubModel,
} from '@/types/anonymously.interface';
import './modal-override.less';
import useRegisterOrLoginGuide from '@/lib/hooks/common/useRegisterOrLoginGuide';
import LoadMoreModal from './LoadMoreModal';
import { ga_logEvent } from '@/shared/ga';
import { EventName } from '@/shared/event-types';
import LoadingIcon from '@/icons/LoadingIcon';
import { InsAccountModel } from '@/types/home';
import useCommentViewPremium from '@/lib/hooks/isPremium/useCommentViewPremium';

interface PostCommentInfoProps {
  item: InstagramQueryPostItemModel;
  /** 是否为移动端预览 */
  isMobilePreview?: boolean;
  /** 控制显示的数量上限 */
  maxDisplayCount?: number | false;
  /** 自定义加载按钮点击事件，将取代默认加载事件 */
  onLoadMoreClick?: () => void;
  getCommnets?: (comments: InstagramPostsCommentsItemModel[]) => void;
  userSubData: InstagramUserSubModel;
  firstGetComments?: (comments: InstagramPostsCommentsItemModel[]) => void;
  alterUpgrade?: () => void;
  notEnoughScoreNotEilte?: () => void;
  notEnoughScoreEilte?: () => void;
  isLogin?: boolean;
  refreshUserData?: () => void;
  setIsChancePost?: (isChancePost: boolean) => void;
  setIsDrawerOpen?: (isDrawerOpen: boolean) => void;
  innerAccountInfo: InsAccountModel;
}

interface SubCommentsData {
  [pk: InstagramPostsCommentsItemModel['pk']]: {
    list: InstagramPostsCommentsItemModel[];
    loading: boolean;
    pagination_token: string;
  };
}

// 原始加载数量选项

// 套餐类型与最大可加载数量映射
const PACKAGE_LIMIT_MAP: Record<string, number> = {
  free: 15,
  basic: 30,
  pro: 120,
  elite: 600,
};

const PostComments = ({
  item,
  isMobilePreview,
  onLoadMoreClick,
  getCommnets,
  userSubData,
  firstGetComments,
  alterUpgrade,
  notEnoughScoreNotEilte,
  notEnoughScoreEilte,
  isLogin,
  refreshUserData,
  setIsChancePost,
  setIsDrawerOpen,
  innerAccountInfo,
}: PostCommentInfoProps) => {
  const { t } = useTranslation();
  const { guideToRegisterOrLogin } = useRegisterOrLoginGuide();

  // 评论数据
  const [commentsData, setCommentsData] = useState<InstagramPostsCommentsItemModel[]>([]);
  const [subCommentsData, setSubCommentsData] = useState<SubCommentsData>({});
  // 评论总数
  const [totalComments, setTotalComments] = useState<number>(0);
  // 分页token
  const [paginationToken, setPaginationToken] = useState<string>('');
  // 加载状态
  const [loading, setLoading] = useState<boolean>(false);
  // 搜索关键词
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  // 回复对话框可见性
  const [replyModalVisible, setReplyModalVisible] = useState<boolean>(false);
  const [replayModalButtonLoading, setReplayModalButtonLoading] = useState<boolean>(false);
  const [loadMoreModalVisible, setLoadMoreModalVisible] = useState<boolean>(false);
  // 新增状态：下拉选项和进度条相关
  const [progressPercent, setProgressPercent] = useState<number>(0);
  const [progressText, setProgressText] = useState<string>('');

  const [loadingPool, setLoadingPool] = useState<boolean>(false); // 是否批量加载中
  const isLoaedCommentRef = useRef<boolean>(false);

  // 请求状态引用
  const fetchingRef = useRef<{ postCode: string | null; isFetching: boolean }>({
    postCode: null,
    isFetching: false,
  });

  const progressTimerRef = useRef<NodeJS.Timeout | null>(null);

  const { isPremium } = useCommentViewPremium();

  useEffect(() => {
    if (getCommnets) {
      getCommnets(commentsData);
    }
    // 同步已加载评论数
    setTotalComments(commentsData.length);
  }, [commentsData]);

  const getCredits = (number: number) => {
    const credits = Math.floor((item.comment_count - totalComments) / 15);
    return credits >= number ? number : credits;
  };

  // 生成实际可用的加载选项，只根据套餐类型，不再动态计算
  const packageLimit = PACKAGE_LIMIT_MAP[userSubData?.package_type || 'free'] || 15;
  const loadOptions = useMemo(() => {
    return [
      {
        value: 15,
        label: '15',
        credits: getCredits(1),
        isLock:
          userSubData?.package_type == 'elite' ||
          userSubData?.package_type == 'pro' ||
          userSubData?.package_type == 'basic' ||
          userSubData?.package_type == '',
      },
      {
        value: 30,
        label: '30',
        credits: getCredits(2),
        isLock:
          userSubData?.package_type == 'elite' ||
          userSubData?.package_type == 'pro' ||
          userSubData?.package_type == 'basic',
      },
      {
        value: 120,
        label: '120',
        credits: getCredits(8),
        isLock: userSubData?.package_type == 'elite' || userSubData?.package_type == 'pro',
      },
      {
        value: 600,
        label: '600',
        credits: getCredits(40),
        isLock: userSubData?.package_type == 'elite',
      },
    ];
  }, [userSubData, item]);

  // 获取评论数据
  const fetchComments = useCallback(
    async (code: string, token?: string) => {
      if (!code) return;

      if (isLoaedCommentRef.current && !token) {
        message.warning(t('All comments are loaded. No credits will be used.'));
        return;
      }

      if (fetchingRef.current.isFetching && fetchingRef.current.postCode === code && !token) {
        return;
      }

      fetchingRef.current = {
        postCode: code,
        isFetching: true,
      };

      if (token) {
        setLoading(true);
      }

      try {
        const res = await fetchInsFamousPostCommentPostsData(code, token, { isPremium, bizFuncName: 'comment_viewer' });
        if (res?.code === 0 && res?.data) {
          const { comments = [], pagination_token } = res.data;
          isLoaedCommentRef.current = true;
          if (firstGetComments) {
            firstGetComments(comments);
          }
          if (token) {
            setCommentsData((prev) => {
              return [...prev, ...comments];
            });
          } else {
            setCommentsData(comments);
          }
          setPaginationToken(pagination_token || '');
        } else {
          setPaginationToken('');
        }
      } catch (error) {
        console.error('Error fetching comments:', error);
      } finally {
        if (token) {
          setLoading(false);
        }
        fetchingRef.current.isFetching = false;
      }
    },
    [t, packageLimit, isPremium]
  );

  // 监听 item.code 变化重新获取数据
  useEffect(() => {
    if (item?.code) {
      setCommentsData([]);
      setPaginationToken('');
      setSearchKeyword('');
      setTotalComments(0); // 重置评论总数
      setProgressPercent(0);
      setProgressText('');
      isLoaedCommentRef.current = false;

      // 清除进度条定时器
      if (progressTimerRef.current) {
        clearInterval(progressTimerRef.current);
        progressTimerRef.current = null;
      }

      fetchComments(item.code);
    }
  }, [item?.code, fetchComments, isLoaedCommentRef]);

  // 组件卸载时清理定时器
  useEffect(() => {
    return () => {
      if (progressTimerRef.current) {
        clearInterval(progressTimerRef.current);
      }
    };
  }, []);

  // 批量加载评论（串行请求池）
  const batchLoadComments = useCallback(
    async (count: number, callback?: (comments: InstagramPostsCommentsItemModel[]) => void) => {
      // 使用当前保存的分页token，确保从上次加载的位置继续
      let pageToken = paginationToken;

      if (isLoaedCommentRef.current && !pageToken) {
        message.warning(t('All comments are loaded. No credits will be used.'));
        return;
      }

      setIsChancePost?.(true);
      setLoadingPool(true);
      // 叠加加载，allComments 初始为当前已加载
      let allComments: InstagramPostsCommentsItemModel[] = [...commentsData];
      let loadSuccess = true; // 标记是否成功完成批量加载

      try {
        const res = await fetchInsFamousPostCommentPostsData(item.code, pageToken, {
          isPremium,
          bizFuncName: 'comment_viewer',
        });
        setLoadingPool(false);
        if (res?.code === 0 && res?.data) {
          isLoaedCommentRef.current = true;
          const { comments = [], pagination_token } = res.data;
          setPaginationToken(pagination_token);
          setCommentsData([...commentsData, ...comments]); // 实时更新展示
          if (!pagination_token) {
            setIsChancePost?.(false);
          }
        } else if (res.code == 25001) {
          loadSuccess = false; // 标记加载失败
          if (userSubData.package_type == 'elite') {
            notEnoughScoreEilte?.();
          } else {
            notEnoughScoreNotEilte?.();
          }
          setIsChancePost?.(false);
          // 点数不够
          if (callback) {
            refreshUserData?.();
            const timer = setTimeout(() => {
              callback(allComments);
              clearTimeout(timer);
            }, 1000);
          }
        } else {
          setPaginationToken('');
          loadSuccess = false; // 标记加载失败
          setIsChancePost?.(false);
        }
      } catch (error) {
        // 当接口报错时，重置分页token，避免使用错误的token
        setPaginationToken('');
        loadSuccess = false; // 标记加载失败
        setIsChancePost?.(false);
        // }
      }
      setLoadingPool(false);
      const timer = setTimeout(() => {
        setProgressText('');
        clearTimeout(timer);
      }, 10);

      // 批量加载完成后调用回调函数
      if (callback && loadSuccess) {
        refreshUserData?.();
        setIsChancePost?.(false);
        const timer = setTimeout(() => {
          callback(allComments);
          clearTimeout(timer);
        }, 1000);
      }
    },
    [commentsData, item.code, packageLimit, t, paginationToken, isPremium]
  );

  const handleLoadCountSelect = (
    count: number,
    callback?: (comments: InstagramPostsCommentsItemModel[]) => void,
    isLock?: boolean
  ) => {
    if (isLock) {
      setIsDrawerOpen?.(false);
      document.getElementById('pricing-title')?.scrollIntoView({ behavior: 'smooth' });
      return;
    }

    if (loadingPool) return;
    guideToRegisterOrLogin(() => {
      if (loading || !item?.code) return;

      const targetCount = Math.min(commentsData.length + count, item.comment_count || 0);
      batchLoadComments(targetCount, callback);
    });
  };

  // 搜索框变化
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchKeyword(e.target.value);
  };

  // 清除搜索
  const clearSearch = () => {
    setSearchKeyword('');
  };

  // 关闭回复对话框
  const handleCloseModal = () => {
    setReplyModalVisible(false);
  };

  const handleInterestedClick = async () => {
    try {
      setReplayModalButtonLoading(true);
      const res = await sendInsFamousRequireAddRequest();
      if (res?.code === 0) {
        // 关闭对话框
        setReplyModalVisible(false);

        // 显示成功消息
        message.success(t("Thank you for your feedback! We've recorded your interest."));
      } else {
        message.error(res.message);
      }
    } catch (error) {
      message.error(t('Failed to record your feedback'));
    } finally {
      setReplayModalButtonLoading(false);
    }
  };

  const getSubComments = (pk: string, paginationToken?: string) => {
    if (!item?.code || !pk) return;
    const subCommentDataItem = subCommentsData[pk];
    const oldList = subCommentDataItem?.list || [];
    setSubCommentsData((prev) => ({
      ...prev,
      [pk]: {
        list: oldList,
        loading: true,
        pagination_token: paginationToken,
      },
    }));
    fetchInsFamousPostSubCommentPostsData(item?.code, pk, paginationToken === '-1' ? '' : paginationToken || '', {
      isPremium,
      bizFuncName: 'comment_viewer',
    }).then((res) => {
      if (res.code === 0) {
        const { comments = [], pagination_token } = res.data || {};
        setSubCommentsData((prev) => ({
          ...prev,
          [pk]: {
            list: [...oldList, ...comments],
            loading: false,
            pagination_token: pagination_token || '',
          },
        }));
      }
    });
  };

  // 过滤后的评论数据
  const filteredComments = useMemo(() => {
    if (!searchKeyword.trim()) return commentsData;

    const keyword = searchKeyword.trim().toLowerCase();
    return commentsData.filter((comment) => {
      const username = comment.media_user_dto?.username?.toLowerCase() || '';
      const fullName = comment.media_user_dto?.full_name?.toLowerCase() || '';
      const text = comment.text?.toLowerCase() || '';

      return username.includes(keyword) || fullName.includes(keyword) || text.includes(keyword);
    });
  }, [commentsData, searchKeyword]);

  // 格式化日期时间，使用dayjs
  const formatDateTime = (dateTimeStr?: string) => {
    if (!dateTimeStr) return '';

    try {
      return dayjs(dateTimeStr).format('YYYY-MM-DD HH:mm:ss');
    } catch (error) {
      return dateTimeStr;
    }
  };

  // 在新标签页打开当前页面，携带 media_name 参数
  const handleOpenInNewTab = (mediaName: string) => {
    guideToRegisterOrLogin(() => {
      window.open(`/instagram-viewer/post-comments?media_name=${mediaName}`, '_blank');
    });
  };

  const renderCommentListContent = (fatherComment?: InstagramPostsCommentsItemModel) => {
    if (fatherComment && !(fatherComment.child_comment_count > 0)) return null;

    const data = fatherComment
      ? subCommentsData[fatherComment.pk]
      : { list: filteredComments, loading, pagination_token: paginationToken };
    if (!fatherComment) {
      if (data?.loading) {
        return <div className="flex justify-center items-center py-16" />;
      }
      if (data.list.length === 0 && !data?.loading) {
        return (
          <div className="flex flex-col items-center justify-center h-60 text-gray-500 p-6">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={searchKeyword ? t('No matching comments found') : t('No comments yet')}
            />
            {searchKeyword && (
              <Button type="link" onClick={clearSearch} className="mt-2">
                {t('Clear search')}
              </Button>
            )}
          </div>
        );
      }
    }

    const remainingCount = fatherComment ? (fatherComment.child_comment_count || 0) - (data?.list?.length || 0) : 0;
    const hasSubComment = remainingCount && (!data || data.pagination_token === '-1');

    return (
      <div>
        {fatherComment?.child_comment_count > 0 && (
          <div className="pl-[68px] flex items-center">
            <span className="w-8 h-[1px] bg-[#D9D9D9] mr-3" />
            <span
              className={`text-[#0C0C0C] opacity-60 text-sm hover:text-[#3469bc] mr-1 ${hasSubComment ? 'cursor-pointer' : 'cursor-not-allowed'}`}
              onClick={(e) => {
                if (!isLogin) {
                  e.stopPropagation();
                  guideToRegisterOrLogin();
                  ga_logEvent(EventName.comment_load_more_btn_to_login);
                  return;
                }
                if (!hasSubComment || data?.loading) return;
                getSubComments(fatherComment.pk, data ? data.pagination_token : '-1');
              }}
            >
              View replies ({hasSubComment ? remainingCount : 0})
            </span>
            {data?.loading && <LoadingIcon size={16} spinner />}
          </div>
        )}
        {data?.list?.map?.((comment) => (
          <div
            key={comment.pk}
            className={`${fatherComment ? 'pl-[68px] pr-4 hover:bg-gray-50 transition-colors' : ''}`}
          >
            <div
              className={`max-md:px-0 max-md:py-2.5 ${fatherComment ? 'py-4' : 'p-4 hover:bg-gray-50 transition-colors'}`}
            >
              <div className="flex items-start gap-3">
                <div
                  className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 cursor-pointer"
                  onClick={() => handleOpenInNewTab(comment.media_user_dto?.username || '')}
                >
                  <LocalImage
                    src={comment.media_user_dto?.profile_pic_url}
                    width={40}
                    height={40}
                    alt={comment.media_user_dto?.full_name || ''}
                  />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center mb-1.5">
                    <span
                      className="font-medium mr-1 cursor-pointer"
                      onClick={() => handleOpenInNewTab(comment.media_user_dto?.username || '')}
                    >
                      {comment.media_user_dto?.full_name || ''}
                    </span>

                    <a
                      className="flex items-center text-gray-500 text-sm"
                      href={`https://www.instagram.com/${comment.media_user_dto?.username}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {/* Instagram图标 */}
                      <Image
                        src="/images/follow/ins-mark.webp"
                        width={16}
                        height={16}
                        alt="Instagram"
                        className="mx-1"
                      />

                      <span>@{comment.media_user_dto?.username}</span>

                      {comment.media_user_dto?.is_verified && (
                        <Image
                          src="/images/follow/blue-mark.webp"
                          width={16}
                          height={16}
                          alt="Verified"
                          className="ml-1"
                        />
                      )}
                    </a>

                    <span className="ml-auto text-xs text-[#B6B6B6]">{formatDateTime(comment.comment_time)}</span>
                  </div>

                  <p className="text-base leading-5 whitespace-pre-line break-words">{comment.text}</p>

                  {comment.comment_like_count > 0 && (
                    <div className="h-5 flex items-center mt-2 text-sm text-[#6D6D6D] max-md:mt-1.5">
                      {comment.comment_like_count > 0 && (
                        <span className="mr-4">
                          {comment.comment_like_count} {t('Likes')}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            {!fatherComment && renderCommentListContent(comment)}
          </div>
        ))}
      </div>
    );
  };

  // 显示的按钮
  const renderLoadMoreButton = useMemo(() => {
    return (
      <Button
        className="w-[80%] mx-auto font-medium h-[40px] max-md:p-0 max-md:text-sm hover:bg-blue-50 relative group"
        onClick={(e) => {
          if (!isLogin) {
            e.stopPropagation();
            guideToRegisterOrLogin();
            ga_logEvent(EventName.comment_load_more_btn_to_login);
            return;
          }
          onLoadMoreClick?.();
        }}
      >
        <div className="flex gap-1 items-center">{t('Load More')}</div>
        <div className="absolute right-4 bottom-[11px] text-xs text-[#B6B6B6] group-hover:text-[#B2A2FE]">
          1 credits
        </div>
      </Button>
    );
  }, [onLoadMoreClick, t, isLogin]);

  const renderProgressButton = useMemo(() => {
    return (
      <div className="w-[80%] mx-auto border gap-2 rounded-md h-[40px] relative overflow-hidden">
        <div
          className="absolute left-0 top-0 h-full bg-[#F1EFFF] rounded-md transition-all duration-300"
          style={{ width: `${progressPercent}%`, zIndex: 1 }}
        />
        <div className="flex justify-center items-center z-10 absolute w-full h-full gap-2">
          <div className="w-4 h-4 border-2 border-purple-400 border-t-transparent rounded-full animate-spin"></div>
          <div>Loading...</div>
        </div>
      </div>
    );
  }, [progressPercent, progressText]);

  const calcProgressPercent = (a: number, b: number) => Math.floor((a / b) * 100);

  const getPaginationComments = async (pageToken: string, currentCommentsCount?: number) => {
    if (isLoaedCommentRef.current && !pageToken) {
      message.warning(t('All comments are loaded. No credits will be used.'));
      return;
    }

    setIsChancePost?.(true);
    setLoadingPool(true);
    // 使用传入的最新评论数量，避免闭包捕获旧值
    const commentsCount = currentCommentsCount ?? commentsData.length;
    setProgressPercent(calcProgressPercent(commentsCount, item.comment_count));
    setProgressText(`${commentsCount}/${item.comment_count}`);

    try {
      const res = await fetchInsFamousPostCommentPostsData(item.code, pageToken, {
        isPremium,
        bizFuncName: 'comment_viewer',
      });
      refreshUserData?.();
      setLoadingPool(false);

      if (res?.code === 0 && res?.data) {
        isLoaedCommentRef.current = true;
        return new Promise((resolve) => {
          const { comments = [], pagination_token } = res.data;
          setPaginationToken(pagination_token);
          if (!pagination_token) {
            setIsChancePost?.(false);
          }
          if (comments?.length > 0) {
            setCommentsData((prevState) => {
              const oldState = pageToken ? prevState : [];
              let newState = [...oldState, ...comments];
              setProgressPercent(calcProgressPercent(newState.length, item.comment_count));
              setProgressText(`${newState.length}/${item.comment_count}`);
              resolve({
                comments: newState,
                pageToken: pagination_token,
              });
              return newState;
            });
            return;
          }
          resolve({
            comments: pageToken ? commentsData : [],
            pageToken: pagination_token,
          });
        });
      }

      if (res.code == 25001) {
        userSubData.package_type == 'elite' ? notEnoughScoreEilte?.() : notEnoughScoreNotEilte?.();
        setIsChancePost?.(false);
        return Promise.reject(false);
      }

      throw new Error('Failed to fetch comments');
    } catch (error) {
      setPaginationToken('');
      setIsChancePost?.(false);
      return Promise.reject(false);
    }
  };

  const exportByFrontend = (
    cb: (comments: InstagramPostsCommentsItemModel[]) => void,
    pageToken?: string,
    currentCount?: number
  ) => {
    if (!pageToken) cb(commentsData);
    getPaginationComments(pageToken, currentCount).then((res: any) => {
      if (res && res.pageToken) {
        exportByFrontend(cb, res.pageToken, res.comments.length);
      } else {
        setProgressPercent(100);
        setTimeout(() => {
          setProgressText('');
        });
        cb(res.comments);
      }
    });
  };

  const exportByBackend = async (
    credits: number,
    cb: (comments: InstagramPostsCommentsItemModel[], isBackEndExport: boolean) => void,
    pageToken?: string
  ) => {
    console.log(innerAccountInfo);
    fetchInsCommentExport(
      item.code,
      {
        page_token: pageToken,
        credits,
        total_num: item.comment_count,
        username: innerAccountInfo.media_name,
        avatar_url: innerAccountInfo.profile_pic_url,
        follower_num: innerAccountInfo.follower_count,
        following_num: innerAccountInfo.following_count,
        post_num: innerAccountInfo.media_count,
        current_list: commentsData.map((i) => ({
          comment_time: i.comment_time,
          pk: i.pk,
          text: i.text,
          username: i.media_user_dto.username,
          profile_pic_url: i.media_user_dto.profile_pic_url,
          post_url: `https://www.instagram.com/p/${item.code}/`,
        })),
      },
      { isPremium, bizFuncName: 'comment_viewer' }
    ).then((res) => {
      if (res.code === 0) {
        cb([], true);
      }
    });
  };

  const handleLoadAll: (
    credits: number,
    cb: (comments: InstagramPostsCommentsItemModel[]) => void,
    pageToken?: string
  ) => void = (credits = 0, cb, pageToken = paginationToken) => {
    if (!pageToken) {
      cb(commentsData);
      setProgressPercent(100);
      return;
    }

    if (credits > userSubData.remaining_credits) {
      userSubData.package_type == 'elite' ? notEnoughScoreEilte?.() : notEnoughScoreNotEilte?.();
      setIsChancePost?.(false);
      setProgressPercent(100);
      return;
    }

    if (item.comment_count < 200) {
      exportByFrontend(cb, pageToken, commentsData.length);
      return;
    }
    exportByBackend(credits, cb, pageToken);
  };

  return (
    <div
      className={cn(
        'w-full flex flex-col border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm h-full',
        'max-md:border-none max-md:shadow-none'
      )}
    >
      <div className="p-4 bg-white  max-md:p-0 max-md:pb-2.5 border-b border-gray-200">
        <div className="flex flex-col gap-2">
          <div className="text-lg font-medium max-md:text-sm flex items-center gap-2">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M9.99989 1.66699C8.90554 1.66699 7.82191 1.88254 6.81086 2.30133C5.79981 2.72012 4.88115 3.33395 4.10733 4.10777C2.54453 5.67057 1.66656 7.79019 1.66656 10.0003C1.65997 11.9245 2.32617 13.7904 3.54989 15.2753L1.88322 16.942C1.76759 17.0592 1.68926 17.208 1.65812 17.3696C1.62697 17.5313 1.64441 17.6986 1.70822 17.8503C1.77744 18.0003 1.88964 18.1263 2.03059 18.2123C2.17155 18.2984 2.33489 18.3406 2.49989 18.3337H9.99989C12.21 18.3337 14.3296 17.4557 15.8924 15.8929C17.4552 14.3301 18.3332 12.2105 18.3332 10.0003C18.3332 7.79019 17.4552 5.67057 15.8924 4.10777C14.3296 2.54497 12.21 1.66699 9.99989 1.66699ZM9.99989 16.667H4.50822L5.28322 15.892C5.43843 15.7359 5.52555 15.5246 5.52555 15.3045C5.52555 15.0843 5.43843 14.8731 5.28322 14.717C4.19204 13.627 3.51253 12.1924 3.36046 10.6577C3.20839 9.12287 3.59317 7.58283 4.44924 6.29993C5.30531 5.01702 6.57971 4.07062 8.05532 3.62197C9.53092 3.17332 11.1164 3.25016 12.5417 3.83942C13.967 4.42868 15.1439 5.49389 15.8719 6.85357C16.5999 8.21325 16.8339 9.78328 16.5342 11.2962C16.2344 12.8091 15.4193 14.1712 14.2278 15.1505C13.0364 16.1299 11.5422 16.6658 9.99989 16.667Z"
                fill="#0C0C0C"
              />
            </svg>
            <span> {item.comment_count}</span>
          </div>
          {!isMobilePreview && (
            <div className="relative flex items-center justify-between">
              <Input
                placeholder={t('Search by username or comment content')}
                prefix={<SearchOutlined className="text-gray-400" />}
                value={searchKeyword}
                onChange={handleSearchChange}
                className="w-[200px] h-9 rounded-lg max-md:h-10"
                allowClear
              />
              <Button
                className="w-fit h-9 rounded-lg max-md:h-10"
                disabled={userSubData?.package_type == 'free'}
                onClick={() => {
                  ga_logEvent(EventName.comment_export_click);
                  if (!isLogin) {
                    guideToRegisterOrLogin();
                    return;
                  }
                  if (userSubData?.package_type == '') {
                    alterUpgrade?.();
                    return;
                  }
                  setLoadMoreModalVisible(true);
                  setProgressPercent(0);
                }}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M7.3335 10.6663H8.66683V4.66634H10.6668L8.00016 1.33301L5.3335 4.66634H7.3335V10.6663Z"
                    fill="#11181A"
                  />
                  <path
                    d="M3.33333 14.6667H12.6667C13.402 14.6667 14 14.0687 14 13.3333V7.33333C14 6.598 13.402 6 12.6667 6H10V7.33333H12.6667V13.3333H3.33333V7.33333H6V6H3.33333C2.598 6 2 6.598 2 7.33333V13.3333C2 14.0687 2.598 14.6667 3.33333 14.6667Z"
                    fill="#11181A"
                  />
                </svg>
                <span className="text-sm">Export</span>
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto bg-white" style={{ maxHeight: 'calc(100% - 120px)' }}>
        {renderCommentListContent()}
      </div>
      <div className="h-[60px] border-t flex items-center justify-center">
        {filteredComments.length > 0 && (
          <div className="p-4 text-center max-md:px-0 max-md:py-2.5 w-full">
            {loadingPool ? (
              renderProgressButton
            ) : !isMobilePreview ? (
              isLogin ? (
                <div onClick={() => handleLoadCountSelect(15, () => {})}>{renderLoadMoreButton}</div>
              ) : (
                renderLoadMoreButton
              )
            ) : (
              renderLoadMoreButton
            )}
          </div>
        )}
      </div>

      <Modal
        open={replyModalVisible}
        onCancel={handleCloseModal}
        footer={null}
        closable={false}
        width={400}
        centered
        title={null}
        className="reply-feature-modal"
        bodyStyle={{ padding: '24px' }}
        style={{ borderRadius: '16px', overflow: 'hidden' }}
      >
        <div>
          <div className="text-xl font-medium text-center relative">
            {t('View Comment Replies?')}
            <CloseOutlined
              className="absolute right-0 top-1 cursor-pointer"
              style={{ fontSize: '16px', color: '#666' }}
              onClick={handleCloseModal}
            />
          </div>
          <p className="text-base text-center leading-relaxed">
            {t(
              'Would you like to see replies within comment threads? If this feature is important to you, please let us know and we will consider adding it in future updates.'
            )}
          </p>

          <div className="flex justify-between gap-4">
            <Button className="not-now-btn flex-1 text-base font-medium" onClick={handleCloseModal}>
              {t('Not Now')}
            </Button>

            <Button
              className="interested-btn flex-1"
              color="primary"
              loading={replayModalButtonLoading}
              onClick={handleInterestedClick}
            >
              {t("I'm Interested")}
            </Button>
          </div>
        </div>
      </Modal>
      {loadMoreModalVisible && (
        <LoadMoreModal
          visible={loadMoreModalVisible}
          onCancel={() => setLoadMoreModalVisible(false)}
          onUpgrade={() => setLoadMoreModalVisible(false)}
          totalComments={item.comment_count}
          loadedComments={filteredComments.length}
          loadOptions={loadOptions}
          commentsData={commentsData}
          progressPercent={progressPercent}
          progressText={progressText}
          resetProgress={() => {
            setProgressPercent(0);
          }}
          code={item.code}
          comment_count={item.comment_count}
          handleLoadAll={handleLoadAll}
        />
      )}
    </div>
  );
};

export default memo(PostComments, (prevProps, nextProps) => {
  return (
    prevProps.item?.code === nextProps.item?.code &&
    prevProps.isMobilePreview === nextProps.isMobilePreview &&
    prevProps.maxDisplayCount === nextProps.maxDisplayCount &&
    prevProps.isLogin === nextProps.isLogin &&
    prevProps.userSubData?.package_type === nextProps.userSubData?.package_type &&
    prevProps.innerAccountInfo === nextProps.innerAccountInfo
  );
});
